import imageCompression from 'browser-image-compression';
import { ref, uploadBytes, getDownloadURL, uploadBytesResumable, getStorage } from 'firebase/storage';
import { storage } from '../firebase';
import { getAuth } from 'firebase/auth';

const MAX_FILE_SIZE_MB = 2;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const COMPRESSION_OPTIONS = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1024,
  useWebWorker: true
};

const FIREBASE_STORAGE_ERRORS = {
  'storage/unauthorized': 'Permission denied. Please check if you are signed in.',
  'storage/canceled': 'Upload was cancelled.',
  'storage/unknown': 'An unknown error occurred.',
  'storage/quota-exceeded': 'Storage quota exceeded.',
};

export const validateImage = (file) => {
  const errors = [];

  // Check file type
  if (!ACCEPTED_TYPES.includes(file.type)) {
    errors.push('Please upload a JPG, PNG, or WebP image.');
  }

  // Check file size (before compression)
  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
    errors.push(`File size must be less than ${MAX_FILE_SIZE_MB}MB.`);
  }

  return errors;
};

export const uploadImage = async ({
  file,
  userId,
  path,
  onProgress = () => {},
  onError = () => {},
  onSuccess = () => {}
}) => {
  try {
    const auth = getAuth();
    const storage = getStorage();  // Get fresh storage instance
    
    // Ensure we have auth
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to upload images');
    }

    console.log('Starting upload process...', { file, userId, path, authUser: auth.currentUser.uid });
    
    // Validate image
    const validationErrors = validateImage(file);
    if (validationErrors.length > 0) {
      throw new Error(validationErrors.join(' '));
    }

    // Compress image
    console.log('Compressing image...');
    const compressedFile = await imageCompression(file, COMPRESSION_OPTIONS);
    
    // Use the original file extension
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const timestamp = Date.now();
    const filename = `${timestamp}.${fileExtension}`;
    
    // Use the provided path directly without appending filename if path ends with photoURL
    const fullPath = path.endsWith('photoURL') ? `${path}/${filename}` : path;
    console.log('Upload path:', fullPath);

    // Create storage reference with auth context
    const storageRef = ref(storage, fullPath.replace(/%2F/g, '/')); // Decode any URL encoding

    // Create metadata
    const metadata = {
      contentType: file.type,
      cacheControl: 'public,max-age=7200',
      customMetadata: {
        userId: auth.currentUser.uid  // Add user context
      }
    };

    // Start upload with metadata
    console.log('Starting upload to Firebase Storage...');
    const uploadTask = uploadBytesResumable(storageRef, compressedFile, metadata);

    return new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        // Progress handler
        (snapshot) => {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          console.log('Upload progress:', progress);
          onProgress(progress);
        },
        // Error handler
        (error) => {
          console.error('Upload error:', error);
          const errorMessage = FIREBASE_STORAGE_ERRORS[error.code] || error.message;
          onError(errorMessage);
          reject(new Error(errorMessage));
        },
        // Completion handler
        async () => {
          try {
            console.log('Upload completed, getting download URL...');
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            
            const imageData = {
              url: downloadURL,
              path: uploadTask.snapshot.ref.fullPath,
              uploadedAt: new Date().toISOString()
            };

            console.log('Upload successful:', imageData);
            onSuccess(downloadURL);
            resolve(imageData);
          } catch (error) {
            console.error('Error getting download URL:', error);
            onError(error.message);
            reject(error);
          }
        }
      );
    });
  } catch (error) {
    console.error('Image upload error:', error);
    onError(error.message);
    throw error;
  }
};

// Helper to check if a URL is a valid image
export const isValidImageUrl = async (url) => {
  try {
    const response = await fetch(url);
    const contentType = response.headers.get('content-type');
    return contentType.startsWith('image/');
  } catch {
    return false;
  }
};

// Add this new function specifically for disciple photos
export const uploadDiscipleImage = async ({
  file,
  discipleId,
  appID,
  onProgress = () => {},
  onError = () => {},
  onSuccess = () => {}
}) => {
  try {
    const auth = getAuth();
    
    // Validate inputs first
    if (!file) {
      throw new Error('No file provided');
    }

    if (!discipleId || !appID) {
      throw new Error('Missing discipleId or appID');
    }

    // Generate filename without using substring
    const timestamp = Date.now();
    const extension = file.type.split('/')[1] || 'png';  // Get extension from mime type
    const filename = `${timestamp}.${extension}`;
    
    // Construct the full path
    const fullPath = `Disciples/${discipleId}/mentorDetails/${appID}/photoURL/${filename}`;
    
    console.log('Uploading to path:', fullPath);

    // Get storage reference
    const storage = getStorage();
    const storageRef = ref(storage, fullPath);

    // Start upload
    const uploadTask = uploadBytesResumable(storageRef, file);

    return new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        // Progress handler
        (snapshot) => {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          onProgress(progress);
        },
        // Error handler
        (error) => {
          console.error('Upload error:', error);
          onError(error.message);
          reject(error);
        },
        // Success handler
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            console.log('File uploaded successfully:', downloadURL);
            onSuccess(downloadURL);
            resolve({ url: downloadURL, path: fullPath });
          } catch (error) {
            console.error('Error getting download URL:', error);
            onError(error.message);
            reject(error);
          }
        }
      );
    });
  } catch (error) {
    console.error('Upload error:', error);
    onError(error.message);
    throw error;
  }
};